import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  Calendar,
  Package,
  Truck,
  FileText,
  Scale,
  Ruler,
  MapPin,
  Ship,
  Navigation,
  Clock,
  ArrowRight,
  Building,
  Building2,
  User,
  AlertCircle,
  Edit3,
  CheckCircle,
  Circle,
  Plus,
  Trash2,
  Upload,
  Shield
} from 'lucide-react';

// Modal pour les informations générales de la saisie
const GeneralModal = ({ isOpen, tempData, setTempData, onClose, onSave, clients, shippingStatuses }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Informations générales
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Client
            </label>
            <select
              value={tempData.clientId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, clientId: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° bon de livraison *
            </label>
            <input
              type="text"
              value={tempData.bonLivraison || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, bonLivraison: e.target.value }))}
              placeholder="BL-001-2024"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° commande client
            </label>
            <input
              type="text"
              value={tempData.numeroCommande || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, numeroCommande: e.target.value }))}
              placeholder="CMD-CLIENT-001"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Statut expédition
            </label>
            <select
              value={tempData.shippingStatusId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, shippingStatusId: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un statut</option>
              {shippingStatuses.map(status => (
                <option key={status.id} value={status.id}>{status.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Date prévue entrée entrepôt
            </label>
            <input
              type="date"
              value={tempData.datePrevueEntreeEntrepot || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, datePrevueEntreeEntrepot: e.target.value }))}
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Date livraison prévue
            </label>
            <input
              type="date"
              value={tempData.dateLivraison || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, dateLivraison: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-port-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={tempData.notes || ''}
            onChange={(e) => setTempData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes générales sur la saisie..."
            rows={3}
            className="input-field"
          />
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={!tempData.bonLivraison}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal pour gérer une étape (Origine, Destination, Entrepôt ATL, Entrepôt ATL Douane)
const StepModal = ({ isOpen, tempData, setTempData, onClose, onSave, stepType, clients }) => {
  if (!isOpen) return null;

  const getStepIcon = () => {
    switch(stepType) {
      case 'Origine': return <MapPin className="h-5 w-5 mr-2" />;
      case 'Destination': return <Ship className="h-5 w-5 mr-2" />;
      case 'Entrepot ATL': return <Building className="h-5 w-5 mr-2" />;
      case 'Entrepot ATL Douane': return <Shield className="h-5 w-5 mr-2" />;
      default: return <Circle className="h-5 w-5 mr-2" />;
    }
  };

  const canLinkToClient = stepType === 'Origine' || stepType === 'Destination';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          {getStepIcon()}
          Étape : {stepType}
        </h3>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Date de l'étape
            </label>
            <input
              type="date"
              value={tempData.dateStep || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, dateStep: e.target.value }))}
              className="input-field"
            />
          </div>
          
          {canLinkToClient && (
            <>
              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Lier à un client (optionnel)
                </label>
                <select
                  value={tempData.entityLinkedPk || ''}
                  onChange={(e) => {
                    const selectedClient = clients.find(c => c.id === e.target.value);
                    setTempData(prev => ({ 
                      ...prev, 
                      entityLinkedPk: e.target.value,
                      entityLinkedName: selectedClient ? selectedClient.name : ''
                    }));
                  }}
                  className="input-field"
                >
                  <option value="">Aucun client lié</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              
              {tempData.entityLinkedPk && (
                <div>
                  <input
                    type="text"
                    value={tempData.entityLinkedName || ''}
                    onChange={(e) => setTempData(prev => ({ ...prev, entityLinkedName: e.target.value }))}
                    placeholder="Nom spécifique pour cette étape"
                    className="input-field"
                  />
                </div>
              )}
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={tempData.notes || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={`Notes pour l'étape ${stepType}...`}
              rows={4}
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Ordre d'affichage
            </label>
            <input
              type="number"
              value={tempData.sortOrder || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
              min="0"
              className="input-field"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal pour le contenu de la saisie
const ContentModal = ({ isOpen, tempData, setTempData, onClose, onSave, conditionnements }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Package className="h-5 w-5 mr-2" />
          Contenu de la saisie
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Description du contenu
              </label>
              <textarea
                value={tempData.description || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée..."
                rows={4}
                className="input-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Conditionnement
              </label>
              <select
                value={tempData.conditionnement || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, conditionnement: e.target.value }))}
                className="input-field"
              >
                <option value="">Sélectionner</option>
                {conditionnements.map(cond => (
                  <option key={cond} value={cond}>{cond}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Poids
              </label>
              <input
                type="text"
                value={tempData.poids || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, poids: e.target.value }))}
                placeholder="2.5 tonnes"
                className="input-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Dimensions
              </label>
              <input
                type="text"
                value={tempData.dimensions || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, dimensions: e.target.value }))}
                placeholder="12.2 x 2.4 x 2.6 m"
                className="input-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-port-gray-700 mb-2">
                Nombre de colis
              </label>
              <input
                type="number"
                value={tempData.nombreColis || ''}
                onChange={(e) => setTempData(prev => ({ ...prev, nombreColis: parseInt(e.target.value) || 0 }))}
                placeholder="1"
                min="0"
                className="input-field"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal pour gérer les transports
const TransportModal = ({ isOpen, tempData, setTempData, onClose, onSave, transporteurs, steps }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Truck className="h-5 w-5 mr-2" />
          Transport
        </h3>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Transporteur *
            </label>
            <select
              value={tempData.transporteurId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, transporteurId: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un transporteur</option>
              {transporteurs.map(transporteur => (
                <option key={transporteur.id} value={transporteur.id}>{transporteur.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              N° de suivi transporteur
            </label>
            <input
              type="text"
              value={tempData.transporteurTrackingNumber || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, transporteurTrackingNumber: e.target.value }))}
              placeholder="Numéro de suivi"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Étape liée (optionnel)
            </label>
            <select
              value={tempData.entryStepId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, entryStepId: e.target.value }))}
              className="input-field"
            >
              <option value="">Aucune étape liée</option>
              {steps.map(step => (
                <option key={step.id} value={step.id}>
                  {step.stepType} - {step.notes ? step.notes.substring(0, 30) + '...' : 'Sans notes'}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={tempData.transportBeforeStep || false}
                onChange={(e) => setTempData(prev => ({ ...prev, transportBeforeStep: e.target.checked }))}
                className="mr-3"
              />
              <span className="text-sm text-port-gray-700">
                Transport avant l'étape
              </span>
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={!tempData.transporteurId}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal pour gérer les documents de douane
const DouaneDocumentModal = ({ isOpen, tempData, setTempData, onClose, onSave, suppliers, steps }) => {
  if (!isOpen) return null;

  const documentTypes = ['T1', 'COA', 'IMA', 'Autre'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Document de douane
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Type de document *
            </label>
            <select
              value={tempData.documentType || 'T1'}
              onChange={(e) => setTempData(prev => ({ ...prev, documentType: e.target.value }))}
              className="input-field"
            >
              {documentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Numéro du document *
            </label>
            <input
              type="text"
              value={tempData.documentNumber || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, documentNumber: e.target.value }))}
              placeholder="Numéro du document"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Date du document
            </label>
            <input
              type="date"
              value={tempData.documentDate || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, documentDate: e.target.value }))}
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Fournisseur *
            </label>
            <select
              value={tempData.supplierId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, supplierId: e.target.value }))}
              className="input-field"
            >
              <option value="">Sélectionner un fournisseur</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>{supplier.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Prix
            </label>
            <input
              type="number"
              step="0.01"
              value={tempData.price || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, price: e.target.value }))}
              placeholder="0.00"
              className="input-field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Étape liée (optionnel)
            </label>
            <select
              value={tempData.entryStepId || ''}
              onChange={(e) => setTempData(prev => ({ ...prev, entryStepId: e.target.value }))}
              className="input-field"
            >
              <option value="">Aucune étape liée</option>
              {steps.map(step => (
                <option key={step.id} value={step.id}>
                  {step.stepType} - {step.notes ? step.notes.substring(0, 30) + '...' : 'Sans notes'}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={tempData.apure || false}
              onChange={(e) => setTempData(prev => ({ ...prev, apure: e.target.checked }))}
              className="mr-3"
            />
            <span className="text-sm text-port-gray-700">
              Document apuré
            </span>
          </label>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={!tempData.documentNumber || !tempData.supplierId}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal pour gérer les documents généraux
const DocumentModal = ({ isOpen, tempData, setTempData, onClose, onSave }) => {
  if (!isOpen) return null;

  const documentTypes = ['Bon de livraison', 'Facture', 'Autre'];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTempData(prev => ({
        ...prev,
        nomFichier: file.name,
        taille: file.size,
        file: file // Pour l'upload ultérieur
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Upload className="h-5 w-5 mr-2" />
          Document
        </h3>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Type de document *
            </label>
            <select
              value={tempData.type || 'Autre'}
              onChange={(e) => setTempData(prev => ({ ...prev, type: e.target.value }))}
              className="input-field"
            >
              {documentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-port-gray-700 mb-2">
              Fichier *
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="input-field"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            />
            {tempData.nomFichier && (
              <div className="mt-2 text-sm text-port-gray-600">
                Fichier sélectionné: {tempData.nomFichier} ({Math.round(tempData.taille / 1024)} KB)
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button 
            onClick={() => onSave(tempData)}
            className="btn-primary"
            disabled={!tempData.nomFichier}
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

const NouvelleSaisie = () => {
  const [formData, setFormData] = useState({
    // Données principales de l'entry
    clientId: '',
    drydocId: '', // Nouveau champ pour lier à un drydoc
    numeroDossier: '', // Calculé automatiquement selon le client et le mois
    shippingStatusId: '',
    datePrevueEntreeEntrepot: '',
    dateReelleEntreeEntrepot: '',
    dateSortieEntrepot: '',
    dateLivraison: '',
    notes: '',
    bonLivraison: '',
    numeroCommande: '',
    
    // Contenu (entry_content)
    content: {
      description: '',
      conditionnement: '',
      poids: '',
      dimensions: '',
      nombreColis: 1
    },
    
    // Étapes (entry_steps)
    steps: [],
    
    // Transports (entry_transports)
    transports: [],
    
    // Documents de douane (entry_douane_documents)
    douaneDocuments: [],
    
    // Documents généraux (entry_documents)
    documents: []
  });

  const [activeModal, setActiveModal] = useState(null);
  const [tempData, setTempData] = useState({});
  const [editingStepIndex, setEditingStepIndex] = useState(-1);
  const [editingTransportIndex, setEditingTransportIndex] = useState(-1);
  const [editingDouaneDocIndex, setEditingDouaneDocIndex] = useState(-1);
  const [editingDocumentIndex, setEditingDocumentIndex] = useState(-1);

  // Données de référence (normalement récupérées via API)
  const clients = [
    { id: 1, name: 'CMA CGM' },
    { id: 2, name: 'MSC' },
    { id: 3, name: 'Evergreen' },
    { id: 4, name: 'COSCO' },
    { id: 5, name: 'Hapag-Lloyd' }
  ];

  const shippingStatuses = [
    { id: 1, label: 'Pré alerte' },
    { id: 2, label: 'Colis réceptionné' },
    { id: 3, label: 'Entrepôt ATL' },
    { id: 4, label: 'Entrepôt ATL douane' },
    { id: 5, label: 'Livré' }
  ];

  const conditionnements = [
    'Conteneur 20\'', 'Conteneur 40\'', 'Conteneur 40\' HC',
    'Palette', 'Carton', 'Sachet', 'Fût', 'Autre'
  ];

  const transporteurs = [
    { id: 1, label: 'Transport Express' },
    { id: 2, label: 'Logistique Pro' },
    { id: 3, label: 'Rapid Delivery' },
    { id: 4, label: 'ATL Transport' },
    { id: 5, label: 'Maritime Solutions' }
  ];

  const suppliers = [
    { id: 1, label: 'Fournisseur A' },
    { id: 2, label: 'Fournisseur B' },
    { id: 3, label: 'Fournisseur C' },
    { id: 4, label: 'Douane France' },
    { id: 5, label: 'Transitaire Europe' }
  ];

  const drydocs = [
    {
      id: 1,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO1234567',
      date_arrivee: '2024-02-01',
      date_depart: '2024-02-15',
      notes: 'Maintenance moteur principal et révision générale',
      status: 'En cours'
    },
    {
      id: 2,
      customer_id: 2,
      customer_name: 'MSC',
      customer_recipient_id: 2,
      customer_recipient_name: 'MSC Terminal',
      imo_number: 'IMO7654321',
      date_arrivee: '2024-02-10',
      date_depart: '2024-02-25',
      notes: 'Réparation coque et peinture',
      status: 'En cours'
    },
    {
      id: 4,
      customer_id: 1,
      customer_name: 'CMA CGM',
      customer_recipient_id: 1,
      customer_recipient_name: 'CMA CGM Marseille',
      imo_number: 'IMO9876543',
      date_arrivee: '2024-02-20',
      date_depart: '2024-03-05',
      notes: 'Rénovation des cabines et espaces communs',
      status: 'Planifié'
    }
  ];

  const stepTypes = ['Origine', 'Destination', 'Entrepot ATL', 'Entrepot ATL Douane'];

  // Fonction pour calculer automatiquement le numéro de dossier
  const calculateNumeroDossier = (clientId) => {
    if (!clientId) return '';
    const currentDate = new Date();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    // Simulation du calcul - en réalité, cela viendrait de l'API
    return `${clientId}${month.toString().padStart(2, '0')}${year}`;
  };

  // Effect pour calculer le numéro de dossier quand le client change
  useEffect(() => {
    if (formData.clientId) {
      const numeroDossier = calculateNumeroDossier(formData.clientId);
      setFormData(prev => ({ ...prev, numeroDossier }));
    } else {
      setFormData(prev => ({ ...prev, numeroDossier: '' }));
    }
  }, [formData.clientId]);

  const openModal = (modalType, currentData = {}, index = -1) => {
    setTempData(currentData);
    setActiveModal(modalType);
    if (modalType === 'step') {
      setEditingStepIndex(index);
    } else if (modalType === 'transport') {
      setEditingTransportIndex(index);
    } else if (modalType === 'douaneDocument') {
      setEditingDouaneDocIndex(index);
    } else if (modalType === 'document') {
      setEditingDocumentIndex(index);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setTempData({});
    setEditingStepIndex(-1);
    setEditingTransportIndex(-1);
    setEditingDouaneDocIndex(-1);
    setEditingDocumentIndex(-1);
  };

  const saveGeneralData = (data) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
    closeModal();
  };

  const saveContentData = (data) => {
    setFormData(prev => ({
      ...prev,
      content: { ...prev.content, ...data }
    }));
    closeModal();
  };

  const saveStepData = (data) => {
    const stepId = editingStepIndex >= 0 ? formData.steps[editingStepIndex].id : `step-${Date.now()}`;
    const newStep = {
      ...data,
      id: stepId,
      stepType: data.stepType || 'Origine'
    };

    setFormData(prev => {
      const newSteps = [...prev.steps];
      if (editingStepIndex >= 0) {
        newSteps[editingStepIndex] = newStep;
      } else {
        newSteps.push(newStep);
      }
      return { ...prev, steps: newSteps.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)) };
    });
    closeModal();
  };

  const saveTransportData = (data) => {
    const newTransport = {
      ...data,
      id: editingTransportIndex >= 0 ? formData.transports[editingTransportIndex].id : Date.now(),
      transportBeforeStep: data.transportBeforeStep || false // S'assurer que c'est toujours un boolean
    };

    setFormData(prev => {
      const newTransports = [...prev.transports];
      if (editingTransportIndex >= 0) {
        newTransports[editingTransportIndex] = newTransport;
      } else {
        newTransports.push(newTransport);
      }
      return { ...prev, transports: newTransports };
    });
    closeModal();
  };

  const saveDouaneDocumentData = (data) => {
    const newDocument = {
      ...data,
      id: editingDouaneDocIndex >= 0 ? formData.douaneDocuments[editingDouaneDocIndex].id : Date.now()
    };

    setFormData(prev => {
      const newDocuments = [...prev.douaneDocuments];
      if (editingDouaneDocIndex >= 0) {
        newDocuments[editingDouaneDocIndex] = newDocument;
      } else {
        newDocuments.push(newDocument);
      }
      return { ...prev, douaneDocuments: newDocuments };
    });
    closeModal();
  };

  const saveDocumentData = (data) => {
    const newDocument = {
      ...data,
      id: editingDocumentIndex >= 0 ? formData.documents[editingDocumentIndex].id : Date.now(),
      dateUpload: new Date().toISOString()
    };

    setFormData(prev => {
      const newDocuments = [...prev.documents];
      if (editingDocumentIndex >= 0) {
        newDocuments[editingDocumentIndex] = newDocument;
      } else {
        newDocuments.push(newDocument);
      }
      return { ...prev, documents: newDocuments };
    });
    closeModal();
  };

  const deleteStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const deleteTransport = (index) => {
    setFormData(prev => ({
      ...prev,
      transports: prev.transports.filter((_, i) => i !== index)
    }));
  };

  const deleteDouaneDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      douaneDocuments: prev.douaneDocuments.filter((_, i) => i !== index)
    }));
  };

  const deleteDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const selectDrydoc = (drydoc) => {
    // Mettre à jour le formData avec les informations du drydoc
    setFormData(prev => ({
      ...prev,
      drydocId: drydoc.id,
      clientId: drydoc.customer_id.toString() // S'assurer que c'est une string pour la cohérence
    }));

    // Créer automatiquement une étape "Destination" avec les informations du bateau
    const destinationStep = {
      id: `step-destination-${Date.now()}`,
      stepType: 'Destination',
      dateStep: '',
      notes: `Livraison pour ${drydoc.customer_recipient_name} en cale sèche (${drydoc.imo_number})`,
      sortOrder: 999, // Mettre en dernier par défaut
      entityLinkedName: drydoc.customer_recipient_name,
      entityLinkedPk: drydoc.customer_recipient_id.toString()
    };

    // Ajouter l'étape aux étapes existantes
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, destinationStep].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    }));

    closeModal();
  };

  const isComplete = (section) => {
    switch(section) {
      case 'general':
        return formData.bonLivraison && formData.shippingStatusId;
      case 'content':
        return formData.content.description || formData.content.conditionnement;
      case 'steps':
        return formData.steps.length > 0;
      case 'transports':
        return formData.transports.length > 0;
      case 'douaneDocuments':
        return formData.douaneDocuments.length > 0;
      case 'documents':
        return formData.documents.length > 0;
      default:
        return false;
    }
  };

  const getStepIcon = (stepType) => {
    switch(stepType) {
      case 'Origine': return <MapPin className="h-6 w-6" />;
      case 'Destination': return <Ship className="h-6 w-6" />;
      case 'Entrepot ATL': return <Building className="h-6 w-6" />;
      case 'Entrepot ATL Douane': return <Shield className="h-6 w-6" />;
      default: return <Circle className="h-6 w-6" />;
    }
  };

  const getStepColor = (stepType) => {
    switch(stepType) {
      case 'Origine': return 'bg-atl-blue-600';
      case 'Destination': return 'bg-atl-green-600';
      case 'Entrepot ATL': return 'bg-port-gray-600';
      case 'Entrepot ATL Douane': return 'bg-yellow-600';
      default: return 'bg-port-gray-400';
    }
  };

  const renderStepsOverview = () => {
    if (formData.steps.length === 0) {
      return (
        <div className="text-center py-8 text-port-gray-500">
          <Navigation className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Aucune étape définie</p>
          <p className="text-sm">Ajoutez des étapes pour suivre la progression</p>
        </div>
      );
    }

    const sortedSteps = [...formData.steps].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    // Fonction pour obtenir les transports liés à une étape
    const getTransportsForStep = (stepId, before = true) => {
      return formData.transports.filter(transport => 
        transport.entryStepId == stepId && (transport.transportBeforeStep || false) === before
      );
    };

    const renderTransportInfo = (transports) => {
      if (transports.length === 0) return null;
      
      return (
        <div className="flex flex-col items-center space-y-1">
          {transports.map(transport => {
            const transporteur = transporteurs.find(t => t.id == transport.transporteurId);
            return (
              <div key={transport.id} className="text-center">
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white">
                  <Truck className="h-4 w-4" />
                </div>
                <div className="text-xs text-port-gray-600 max-w-20 truncate">
                  {transporteur?.label || 'Transporteur'}
                </div>
                {transport.transporteurTrackingNumber && (
                  <div className="text-xs text-port-gray-500 max-w-20 truncate">
                    {transport.transporteurTrackingNumber}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    // Fonction pour afficher tous les transports non liés aux étapes
    const getUnlinkedTransports = () => {
      return formData.transports.filter(transport => !transport.entryStepId);
    };

    const unlinkedTransports = getUnlinkedTransports();

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center space-x-4 py-4 flex-wrap">
          {sortedSteps.map((step, index) => {
            const transportsBefore = getTransportsForStep(step.id, true);
            const transportsAfter = getTransportsForStep(step.id, false);
            
            return (
              <React.Fragment key={step.id}>
                {/* Transports avant l'étape */}
                {transportsBefore.length > 0 && (
                  <>
                    {renderTransportInfo(transportsBefore)}
                    <ArrowRight className="h-4 w-4 text-orange-400" />
                  </>
                )}
                
                {/* L'étape elle-même */}
                <div className="flex flex-col items-center space-y-2">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${getStepColor(step.stepType)}`}>
                    {getStepIcon(step.stepType)}
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-port-gray-900">{step.stepType}</div>
                    <div className="text-sm text-port-gray-600 max-w-32 truncate">
                      {step.entityLinkedName || ''}
                    </div>
                    {step.dateStep && (
                      <div className="text-xs text-port-gray-500 flex items-center mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(step.dateStep).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Transports après l'étape */}
                {transportsAfter.length > 0 && (
                  <>
                    <ArrowRight className="h-4 w-4 text-orange-400" />
                    {renderTransportInfo(transportsAfter)}
                  </>
                )}
                
                {/* Flèche vers l'étape suivante */}
                {index < sortedSteps.length - 1 && (
                  <ArrowRight className="h-6 w-6 text-port-gray-400" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Transports non liés aux étapes */}
        {unlinkedTransports.length > 0 && (
          <div className="border-t pt-4">
            <div className="text-center mb-2">
              <span className="text-sm text-port-gray-600 bg-port-gray-100 px-3 py-1 rounded-full">
                Transports non liés aux étapes
              </span>
            </div>
            <div className="flex items-center justify-center space-x-4">
              {renderTransportInfo(unlinkedTransports)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-3xl font-bold text-port-gray-900">Nouvelle saisie</h1>
            <p className="text-port-gray-600">Créer une nouvelle saisie ATL</p>
          </div>
          <button 
            onClick={() => openModal('drydocSelection')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              formData.drydocId 
                ? 'bg-atl-blue-600 text-white hover:bg-atl-blue-700' 
                : 'bg-atl-blue-50 text-atl-blue-700 hover:bg-atl-blue-100'
            }`}
          >
            <Ship className="h-5 w-5" />
            <span>
              {formData.drydocId 
                ? `Drydoc #${formData.drydocId}` 
                : 'Concerne un Drydoc'
              }
            </span>
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <button className="btn-secondary flex items-center space-x-2">
            <X className="h-5 w-5" />
            <span>Annuler</span>
          </button>
          <button className="btn-primary flex items-center space-x-2">
            <Save className="h-5 w-5" />
            <span>Enregistrer</span>
          </button>
        </div>
      </div>

      {/* Aperçu des étapes */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-port-gray-900 mb-4 flex items-center">
          <Navigation className="h-6 w-6 mr-2" />
          Aperçu des étapes
        </h2>
        {renderStepsOverview()}
      </div>

      {/* Dashboard avec sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Informations générales */}
        <div 
          onClick={() => openModal('general', {
            clientId: formData.clientId,
            bonLivraison: formData.bonLivraison,
            numeroCommande: formData.numeroCommande,
            shippingStatusId: formData.shippingStatusId,
            datePrevueEntreeEntrepot: formData.datePrevueEntreeEntrepot,
            dateLivraison: formData.dateLivraison,
            notes: formData.notes
          })}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Informations générales</h3>
            </div>
            {isComplete('general') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            {formData.drydocId && (
              <div className="flex justify-between p-2 bg-atl-blue-50 rounded mb-2">
                <span className="text-atl-blue-700 font-medium flex items-center">
                  <Ship className="h-4 w-4 mr-1" />
                  Drydoc:
                </span>
                <span className="text-atl-blue-900 font-medium">
                  {drydocs.find(d => d.id == formData.drydocId)?.customer_recipient_name}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-port-gray-600">Client:</span>
              <span className={formData.clientId ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.clientId ? clients.find(c => c.id == formData.clientId)?.name : 'Non défini'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-port-gray-600">N° dossier:</span>
              <span className={formData.numeroDossier ? 'text-atl-green-600 font-medium' : 'text-port-gray-400'}>
                {formData.numeroDossier || 'Auto-généré'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-port-gray-600">Bon livraison:</span>
              <span className={formData.bonLivraison ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.bonLivraison || 'Non défini'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-port-gray-600">Statut:</span>
              <span className={formData.shippingStatusId ? 'text-port-gray-900 font-medium' : 'text-port-gray-400'}>
                {formData.shippingStatusId ? shippingStatuses.find(s => s.id == formData.shippingStatusId)?.label : 'Non défini'}
              </span>
            </div>
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Contenu */}
        <div 
          onClick={() => openModal('content', formData.content)}
          className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">Contenu</h3>
            </div>
            {isComplete('content') ? 
              <CheckCircle className="h-6 w-6 text-atl-green-600" /> : 
              <Circle className="h-6 w-6 text-port-gray-400" />
            }
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-port-gray-600">Description:</span>
              <span className="ml-2 text-port-gray-900 font-medium">
                {formData.content.description || 'Non définie'}
              </span>
            </div>
            <div>
              <span className="text-port-gray-600">Conditionnement:</span>
              <span className="ml-2 text-port-gray-900 font-medium">
                {formData.content.conditionnement || 'Non défini'}
              </span>
            </div>
            {formData.content.poids && (
              <div>
                <span className="text-port-gray-600">Poids:</span>
                <span className="ml-2 text-port-gray-900 font-medium">{formData.content.poids}</span>
              </div>
            )}
            {formData.content.dimensions && (
              <div>
                <span className="text-port-gray-600">Dimensions:</span>
                <span className="ml-2 text-port-gray-900 font-medium">{formData.content.dimensions}</span>
              </div>
            )}
          </div>
          
          <div className="mt-4 flex items-center text-atl-blue-600">
            <Edit3 className="h-4 w-4 mr-1" />
            <span className="text-sm font-medium">Modifier</span>
          </div>
        </div>

        {/* Étapes */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Navigation className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">
                Étapes ({formData.steps.length})
              </h3>
            </div>
            <button
              onClick={() => openModal('stepType')}
              className="btn-primary-sm flex items-center space-x-1"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>
          
          {formData.steps.length > 0 ? (
            <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
              {formData.steps
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .map((step, index) => (
                <div key={step.id} className="flex items-center justify-between p-2 bg-port-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${getStepColor(step.stepType)}`}>
                      {step.sortOrder || index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-port-gray-900">{step.stepType}</div>
                      <div className="text-port-gray-600 text-xs">
                        {step.entityLinkedName || step.notes?.substring(0, 20) + '...' || 'Sans détails'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal('step', step, index);
                      }}
                      className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteStep(index);
                      }}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-port-gray-500 text-sm">
              Aucune étape définie. Cliquez sur "Ajouter" pour commencer.
            </p>
          )}
        </div>

        {/* Transports */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Truck className="h-8 w-8 text-atl-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">
                Transports ({formData.transports.length})
              </h3>
            </div>
            <button
              onClick={() => openModal('transport')}
              className="btn-primary-sm flex items-center space-x-1"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>
          
          {formData.transports.length > 0 ? (
            <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
              {formData.transports.map((transport, index) => (
                <div key={transport.id} className="flex items-center justify-between p-2 bg-port-gray-50 rounded">
                  <div>
                    <div className="font-medium text-port-gray-900">
                      {transporteurs.find(t => t.id == transport.transporteurId)?.label || 'Transporteur inconnu'}
                    </div>
                    <div className="text-port-gray-600 text-xs">
                      {transport.transporteurTrackingNumber || 'Pas de suivi'}
                      {transport.entryStepId && (transport.transportBeforeStep ? ' • Avant étape' : ' • Après étape')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal('transport', transport, index);
                      }}
                      className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTransport(index);
                      }}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-port-gray-500 text-sm">
              Aucun transport défini. Cliquez sur "Ajouter" pour commencer.
            </p>
          )}
        </div>

        {/* Documents de douane */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-yellow-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">
                Documents douane ({formData.douaneDocuments.length})
              </h3>
            </div>
            <button
              onClick={() => openModal('douaneDocument')}
              className="btn-primary-sm flex items-center space-x-1"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>
          
          {formData.douaneDocuments.length > 0 ? (
            <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
              {formData.douaneDocuments.map((doc, index) => (
                <div key={doc.id} className="flex items-center justify-between p-2 bg-port-gray-50 rounded">
                  <div>
                    <div className="font-medium text-port-gray-900">
                      {doc.documentType} - {doc.documentNumber}
                    </div>
                    <div className="text-port-gray-600 text-xs">
                      {suppliers.find(s => s.id == doc.supplierId)?.label || 'Fournisseur inconnu'}
                      {doc.price && ` • ${doc.price}€`}
                      {doc.apure && ' • Apuré'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal('douaneDocument', doc, index);
                      }}
                      className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteDouaneDocument(index);
                      }}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-port-gray-500 text-sm">
              Aucun document de douane. Cliquez sur "Ajouter" pour commencer.
            </p>
          )}
        </div>

        {/* Documents généraux */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Upload className="h-8 w-8 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-port-gray-900">
                Documents ({formData.documents.length})
              </h3>
            </div>
            <button
              onClick={() => openModal('document')}
              className="btn-primary-sm flex items-center space-x-1"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>
          
          {formData.documents.length > 0 ? (
            <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
              {formData.documents.map((doc, index) => (
                <div key={doc.id} className="flex items-center justify-between p-2 bg-port-gray-50 rounded">
                  <div>
                    <div className="font-medium text-port-gray-900">
                      {doc.type} - {doc.nomFichier}
                    </div>
                    <div className="text-port-gray-600 text-xs">
                      {doc.taille && `${Math.round(doc.taille / 1024)} KB`}
                      {doc.dateUpload && ` • ${new Date(doc.dateUpload).toLocaleDateString('fr-FR')}`}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal('document', doc, index);
                      }}
                      className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteDocument(index);
                      }}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-port-gray-500 text-sm">
              Aucun document. Cliquez sur "Ajouter" pour commencer.
            </p>
          )}
        </div>
      </div>

      {/* Modals */}
      <GeneralModal 
        isOpen={activeModal === 'general'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveGeneralData}
        clients={clients}
        shippingStatuses={shippingStatuses}
      />
      
      <ContentModal 
        isOpen={activeModal === 'content'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveContentData}
        conditionnements={conditionnements}
      />
      
      <StepModal 
        isOpen={activeModal === 'step'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveStepData}
        stepType={tempData.stepType}
        clients={clients}
      />
      
      <TransportModal 
        isOpen={activeModal === 'transport'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveTransportData}
        transporteurs={transporteurs}
        steps={formData.steps}
      />
      
      <DouaneDocumentModal 
        isOpen={activeModal === 'douaneDocument'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveDouaneDocumentData}
        suppliers={suppliers}
        steps={formData.steps}
      />
      
      <DocumentModal 
        isOpen={activeModal === 'document'}
        tempData={tempData}
        setTempData={setTempData}
        onClose={closeModal}
        onSave={saveDocumentData}
      />

      {/* Modal de sélection du type d'étape */}
      {activeModal === 'stepType' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Choisir le type d'étape</h3>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {stepTypes.map(stepType => (
                <button
                  key={stepType}
                  onClick={() => {
                    setTempData({ stepType });
                    setActiveModal('step');
                  }}
                  className="p-4 border border-port-gray-200 rounded-lg hover:border-atl-blue-300 hover:bg-atl-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${getStepColor(stepType)}`}>
                      {getStepIcon(stepType)}
                    </div>
                    <span className="font-medium text-port-gray-900">{stepType}</span>
                  </div>
                  <p className="text-xs text-port-gray-600">
                    {stepType === 'Origine' && 'Point de départ de la livraison'}
                    {stepType === 'Destination' && 'Point d\'arrivée final'}
                    {stepType === 'Entrepot ATL' && 'Passage par l\'entrepôt ATL'}
                    {stepType === 'Entrepot ATL Douane' && 'Traitement douanier ATL'}
                  </p>
                </button>
              ))}
            </div>
            
            <div className="flex justify-end">
              <button onClick={closeModal} className="btn-secondary">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de sélection de drydoc */}
      {activeModal === 'drydocSelection' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Ship className="h-6 w-6 mr-2 text-atl-blue-600" />
              Sélectionner un Drydoc
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-port-gray-600">
                Choisissez le drydoc (bateau en cale sèche) concerné par cette saisie. 
                Une étape "Destination" sera automatiquement créée avec les informations du bateau.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {drydocs.map(drydoc => (
                <div 
                  key={drydoc.id}
                  onClick={() => selectDrydoc(drydoc)}
                  className="p-4 border border-port-gray-200 rounded-lg hover:border-atl-blue-300 hover:bg-atl-blue-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <Ship className="h-8 w-8 text-atl-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-port-gray-900">{drydoc.customer_recipient_name}</h4>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            drydoc.status === 'En cours' ? 'bg-atl-blue-100 text-atl-blue-800' :
                            drydoc.status === 'Planifié' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-atl-green-100 text-atl-green-800'
                          }`}>
                            {drydoc.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center text-port-gray-600">
                            <Building2 className="h-4 w-4 mr-1" />
                            <span>Client: {drydoc.customer_name}</span>
                          </div>
                          <div className="flex items-center text-port-gray-600">
                            <Ship className="h-4 w-4 mr-1" />
                            <span>IMO: {drydoc.imo_number}</span>
                          </div>
                          <div className="flex items-center text-port-gray-600">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>
                              Du {new Date(drydoc.date_arrivee).toLocaleDateString('fr-FR')} 
                              au {new Date(drydoc.date_depart).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          {drydoc.notes && (
                            <div className="text-port-gray-600 mt-2">
                              <span className="font-medium">Notes:</span> {drydoc.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end">
              <button onClick={closeModal} className="btn-secondary">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NouvelleSaisie;

